/**
 * フロー図の全画面表示。PJ から見た画面（`PjFlowPage.tsx`）と現在版 1 枚
 * （`FlowPage.tsx`）の両方が使う。
 *
 * **図が枠より大きいのは PJ を見ているときだけではない。** 列が 8 本あるフローは枠の 3 倍以上の
 * 幅になり、現在版を読むときも同じだけはみ出す。全画面を PJ 側にだけ置くと、同じ図を同じ理由で
 * 広げたい現在版で広げられない。片方にだけ持たせると、もう片方が同じ覆いを書き直すことにもなる。
 *
 * **ブラウザの Fullscreen API は使わない。** API の側は呼び出しが Promise で、利用者の操作から
 * 離れた呼び出しや権限で拒まれる経路があり、「押したのに全画面にならない」失敗を扱う分岐が
 * 増える。この画面が全画面に求めるのはヘッダー・サイドバー・詳細ペインを図の外へ追い出して器を
 * 広げることだけで、ブラウザの枠まで消す必要は無い。
 *
 * **覆いは `body` の直下へ出す。** `position: fixed` は、祖先に `none` でない `transform` が
 * あるとビューポートではなくその祖先を基準に置かれる（CSS の包含ブロックの規則）。共有シェルの
 * 本文ラッパーは登場のアニメーションの後も恒等の `transform` を残すので、木の中へ置いたままだと
 * 覆いが本文の枠に閉じ込められる。実際に本番でそうなり、ヘッダーとサイドバーが残っていた。
 *
 * **層は共有の段（`--z-modal`）から引き、数値を直に書かない。** 共有シェルのサイドバーは
 * `--z-sticky`（100）に居るので、数値で `z-50` と書くと覆いはその下へ潜る。これも本番で起きた。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図を画面いっぱいの覆いへ出し、Esc と操作列から戻せるようにする。
 * @graph-connects none
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { StateToggleButton } from '../components/spec/StateToggleButton.js';

/** 通常表示の外枠。図とその操作列を縦に積むだけの器。 @graph-connects none */
const NORMAL_CLASS = 'flex min-w-0 flex-col gap-2';

/**
 * 全画面表示の外枠。画面を覆い、図の外に置くものを操作列だけにする。
 *
 * 背景を塗るのは、覆いが透けると下の画面の文字が図に重なって読めなくなるためである。色も層も
 * このアプリが既に使っている CSS 変数から引く。
 * @graph-connects none
 */
const FULLSCREEN_CLASS =
  'fixed inset-0 z-[var(--z-modal)] flex min-w-0 flex-col gap-2 overflow-auto bg-[var(--color-bg-base)] p-4';

/** 全画面の状態と、それを操作する口。 */
export interface FlowFullscreen {
  /** いま全画面かどうか。器の高さの上限を決めるのに使う。 */
  fullscreen: boolean;
  /** 全画面へ入る / 出るを切り替える。 */
  toggle: () => void;
}

/**
 * 全画面の状態を持つ。
 *
 * 表示の状態なので URL にも保存領域にも出さない。出すと「どのフローを見ているか」以外の情報が
 * 導線に混ざり、共有した URL が相手の画面で別の見え方になる。
 *
 * 覆いで作った全画面は Esc でも閉じられるようにする。ブラウザの全画面と見た目が同じなので、
 * 読み手は Esc で戻れると期待する。期待どおりに閉じないと、押しても何も起きない操作を何度か
 * 試したうえでボタンを探すことになる。
 *
 * @returns 全画面の状態と切り替えの口
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図の全画面の状態を持ち、Esc でも戻せるようにする。
 * @graph-connects none
 */
export function useFlowFullscreen(): FlowFullscreen {
  const [fullscreen, setFullscreen] = useState(false);

  const toggle = useCallback(() => {
    setFullscreen((current) => !current);
  }, []);

  useEffect(() => {
    const exitOnEscape = (event: KeyboardEvent) => {
      if (fullscreen && event.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', exitOnEscape);
    return () => {
      window.removeEventListener('keydown', exitOnEscape);
    };
  }, [fullscreen]);

  return { fullscreen, toggle };
}

/** `FlowFullscreenFrame` の props。 */
export interface FlowFullscreenFrameProps {
  /** いま全画面かどうか。 */
  fullscreen: boolean;
  /** 覆いの中身（操作列と図）。 */
  children: ReactNode;
}

/**
 * 図とその操作列を包む器。全画面のときだけ `body` の直下の覆いになる。
 *
 * @param props 全画面かどうかと中身
 * @returns 通常表示ならそのままの器、全画面なら `body` 直下の覆い
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図の器を、全画面のときだけ body 直下の覆いとして描く。
 * @graph-connects none
 */
export function FlowFullscreenFrame({ fullscreen, children }: FlowFullscreenFrameProps) {
  const frame = (
    <div
      className={fullscreen ? FULLSCREEN_CLASS : NORMAL_CLASS}
      data-flow-fullscreen={String(fullscreen)}
    >
      {children}
    </div>
  );

  return fullscreen ? createPortal(frame, document.body) : frame;
}

/** `FlowFullscreenToggle` の props。 */
export interface FlowFullscreenToggleProps {
  /** いま全画面かどうか。 */
  fullscreen: boolean;
  /** 押したときに切り替える。 */
  onToggle: () => void;
}

/**
 * 全画面へ入る / 出るボタン。
 *
 * 押し下げの状態を `aria-pressed` で持つ。文言だけで状態を表すと、読み上げでは「いま全画面か」が
 * 押すまで分からない。文言は押すと起きること（全画面で見る ／ 全画面を終わる）を書き、形は
 * 比較や一覧の絞り込みと同じ `StateToggleButton` に任せる。
 *
 * 文言は PJ の名前空間から出さない。このボタンは PJ から見た比較と現在版 1 枚の両方に
 * 出るので、`pjFlow.*` のままだと現在版の画面が PJ の語彙を引くことになる。
 *
 * @param props 全画面かどうかと切り替えの口
 * @returns 全画面の切り替えボタン
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図の全画面を切り替えるボタンを描く。
 * @graph-connects StateToggleButton [calls] 押し下げで文言を入れ替えるボタン
 */
export function FlowFullscreenToggle({ fullscreen, onToggle }: FlowFullscreenToggleProps) {
  const { t } = useTranslation();

  return (
    <StateToggleButton
      pressed={fullscreen}
      idleLabel={t('flowFullscreen.enter')}
      pressedLabel={t('flowFullscreen.exit')}
      onToggle={onToggle}
    />
  );
}
